/**
 * Ground-footprint helpers for a HIDDEN-GLOBE scene.
 *
 * ⚠ WHY THIS FILE EXISTS — do not "simplify" it back to computeViewRectangle().
 *
 * This scene sets `scene.globe.show = false` and renders Google Photorealistic
 * 3D Tiles as a primitive instead. On such a scene:
 *
 *   viewer.camera.computeViewRectangle()  ->  THE WHOLE GLOBE
 *
 * MEASURED on the live globe 2026-08-17, camera at 2500 m over Houston with a
 * -45 deg pitch: computeViewRectangle() returned spanLat 180 / spanLon 360,
 * i.e. centre (0,0) in the Gulf of Guinea. It does not return undefined, it
 * returns a confidently wrong answer — so a `if (rect)` guard sails straight
 * past it. That silently made the sun study compute for (0,0) ("SUN BELOW
 * HORIZON" over daylit Houston) and would have pinned the threat heatmap at
 * "zoom in for threat data" forever, since a 180x360 bbox always trips the
 * too-wide guard.
 *
 * This is the same hidden-globe trap recorded for the rejected OSM particle
 * dots, whose `getViewportBbox` used `scene.globe.pick()` — null on a hidden
 * globe. The fix there and here is the same: pick against the ELLIPSOID.
 */

declare const Cesium: typeof import("cesium");

export interface GroundRect {
  w: number;
  s: number;
  e: number;
  n: number;
}

/** Screen sample grid (fractions of canvas) used to trace the ground footprint. */
const SAMPLES: Array<[number, number]> = [
  [0.5, 0.5],
  [0.08, 0.08], [0.92, 0.08], [0.08, 0.92], [0.92, 0.92],
  [0.5, 0.08], [0.5, 0.92], [0.08, 0.5], [0.92, 0.5],
];

function ellipsoidOf(viewer: any) {
  return viewer.scene?.globe?.ellipsoid ?? Cesium.Ellipsoid.WGS84;
}

/**
 * Where the camera is actually looking on the ground, in degrees.
 * Falls back to the sub-camera point when the view is mostly sky.
 *
 * @param maxSpanDeg clamp on the returned footprint, so a horizon-grazing
 *        view cannot produce a continental query.
 */
export function groundRectDegrees(viewer: any, maxSpanDeg = 12): GroundRect | null {
  if (!viewer || viewer.isDestroyed?.()) return null;
  const camera = viewer.camera;
  const canvas = viewer.scene?.canvas;
  const ell = ellipsoidOf(viewer);

  const cw = canvas?.clientWidth ?? 0;
  const ch = canvas?.clientHeight ?? 0;

  const lons: number[] = [];
  const lats: number[] = [];

  if (cw > 0 && ch > 0) {
    for (const [fx, fy] of SAMPLES) {
      const hit = camera.pickEllipsoid(new Cesium.Cartesian2(fx * cw, fy * ch), ell);
      if (!hit) continue; // that sample ray missed the earth (sky)
      const carto = Cesium.Cartographic.fromCartesian(hit, ell);
      if (!carto) continue;
      lons.push(Cesium.Math.toDegrees(carto.longitude));
      lats.push(Cesium.Math.toDegrees(carto.latitude));
    }
  }

  // Fallback: no usable picks (canvas not laid out, or looking at the sky).
  // Use the point under the camera with a span derived from altitude.
  if (lats.length < 2) {
    const carto = camera.positionCartographic;
    if (!carto) return null;
    const lat = Cesium.Math.toDegrees(carto.latitude);
    const lon = Cesium.Math.toDegrees(carto.longitude);
    const half = Math.max(0.005, Math.min(maxSpanDeg / 2, carto.height / 111_320));
    return { w: lon - half, s: lat - half, e: lon + half, n: lat + half };
  }

  let w = Math.min(...lons);
  let e = Math.max(...lons);
  let s = Math.min(...lats);
  let n = Math.max(...lats);

  // Antimeridian: picks straddling +/-180 produce a bogus full-width span.
  // Fall back to a window around the centre rather than querying the planet.
  if (e - w > 180) {
    const carto = camera.positionCartographic;
    const lon = Cesium.Math.toDegrees(carto.longitude);
    const half = Math.min(maxSpanDeg / 2, Math.max(0.01, (n - s) / 2));
    w = lon - half;
    e = lon + half;
  }

  // Clamp an oblique/horizon view down to the area around the centre.
  const cLat = (n + s) / 2;
  const cLon = (e + w) / 2;
  if (n - s > maxSpanDeg) {
    s = cLat - maxSpanDeg / 2;
    n = cLat + maxSpanDeg / 2;
  }
  if (e - w > maxSpanDeg) {
    w = cLon - maxSpanDeg / 2;
    e = cLon + maxSpanDeg / 2;
  }

  // Degenerate (straight-down at very low altitude) — give it real area.
  if (n - s < 1e-4) { s = cLat - 0.002; n = cLat + 0.002; }
  if (e - w < 1e-4) { w = cLon - 0.002; e = cLon + 0.002; }

  return { w, s, e, n };
}

/**
 * The ground point the operator is actually looking AT — centre-screen pick,
 * falling back to the sub-camera point. This is the position solar geometry
 * must be computed for; the camera's own lat/lon is a decent proxy but the
 * aim point is what the operator means by "here".
 */
export function aimPointDegrees(viewer: any): { lat: number; lon: number } | null {
  if (!viewer || viewer.isDestroyed?.()) return null;
  const camera = viewer.camera;
  const canvas = viewer.scene?.canvas;
  const ell = ellipsoidOf(viewer);

  const cw = canvas?.clientWidth ?? 0;
  const ch = canvas?.clientHeight ?? 0;

  if (cw > 0 && ch > 0) {
    const hit = camera.pickEllipsoid(new Cesium.Cartesian2(cw / 2, ch / 2), ell);
    if (hit) {
      const carto = Cesium.Cartographic.fromCartesian(hit, ell);
      if (carto) {
        return {
          lat: Cesium.Math.toDegrees(carto.latitude),
          lon: Cesium.Math.toDegrees(carto.longitude),
        };
      }
    }
  }

  const carto = camera.positionCartographic;
  if (!carto) return null;
  return {
    lat: Cesium.Math.toDegrees(carto.latitude),
    lon: Cesium.Math.toDegrees(carto.longitude),
  };
}
